import { AnswerCountSelectorField } from '@/components/common/field/AnswerCountSelectorField';
import { AppDispatch } from '@/app/store';
import { AuthorDTO } from '@/types/AuthorDTO';
import { Button, Grid, Theme, Typography } from '@mui/material';
import { CreateFormAttachments } from '@/components/create/letter/CreateFormAttachments';
import { CreateFormProps } from '@/types/create/CreateFormProps';
import {
  DEFAULT_MAX_PHOTO_COUNT,
  MAX_LETTER_COUNT,
  MESSAGE_FETCH_ERROR,
  MESSAGE_PHOTO_SIZE_EXCEEDED,
} from '@/utils/validation-utils';
import { DepartmentDTO } from '@/types/DepartmentDTO';
import { ErrorDTO } from '@/types/ErrorDTO';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { FixedButton } from '@/components/common/button/FixedButton';
import { InfoIcon } from '@/app/icons/InfoIcon';
import { LabeledCheckBox } from '@/components/common/LabeledCheckBox';
import { LetterAlert } from '@/components/create/letter/LetterAlert';
import { LetterDraftIdDTO } from '@/types/letterDraft/LetterDraftIdDTO';
import { LetterRulesDialog } from '@/components/create/LetterRulesDialog';
import { LetterTextField } from '@/components/create/letter/LetterTextField';
import { MobileBar } from '@/components/home/MobileBar';
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { PhotoStageDTO } from '@/types/create/PhotoStageDTO';
import { REQUEST_ENTITY_TOO_LARGE } from '@/constants/HttpStatus';
import { RegionDTO } from '@/types/RegionDTO';
import { SaveLetterDraftDTO } from '@/types/letterDraft/SaveLetterDraftDTO';
import { SenderDTO } from '@/types/SenderDTO';
import { SerializedError } from '@reduxjs/toolkit';
import { StagedPhotoDTO } from '@/types/create/StagedPhotoDTO';
import { SudokuDifficulty } from '@/types/create/SudokuDifficulty';
import { SudokuType, resolveSudokuTypeOptions } from '@/types/create/SudokuType';
import { TittleInfo } from '@/components/create/TittleInfo';
import { TypographyLink } from '@/components/common/TypographyLink';
import { UserDTO } from '@/types/user/UserDTO';
import {
  addStagedPhoto,
  getAnswerOrder,
  getAnswersCount,
  getAuthor,
  getCrosswordAttached,
  getCrosswordCount,
  getDepartment,
  getLetterDraftId,
  getLetterText,
  getNewsAttached,
  getRegion,
  getSender,
  getSportsNewsAttached,
  getStagedPhotos,
  getSudokuAttached,
  getSudokuCount,
  getSudokuDifficulty,
  getSudokuType,
  removeStagedPhoto,
  setAnswerOrderedForm,
  setAnswersCountForm,
  setCrosswordAttachedState,
  setCrosswordCountState,
  setLetterDraftId,
  setLetterTextForm,
  setNewsAttachedState,
  setSportsNewsAttachedState,
  setSudokuAttachedState,
  setSudokuCountState,
  setSudokuDifficultyState,
  setSudokuTypeState,
} from '@/services/createFormSlice';
import { calculateAdditionalCost } from '@/utils/price-utils';
import { clearBalanceByLetterPageState } from '@/services/sbpSlice';
import { emojiValidation } from '@/validation/emojiValidation';
import { formatCurrency, isBlank } from '@/utils/string-utils';
import { getCrosswordCost, getLetterCost, getNewsCost, getPhotoCost, getSudokuCost } from '@/utils/currency-utils';
import { getCurrentUser } from '@/services/authSlice';
import { getInboxLetterRoute } from '@/app/routes';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useDebounce } from '@/app/hooks/useDebounce';
import { useGetClientInfoQuery } from '@/services/api/createFormApiSlice';
import { useSaveLetterDraftMutation } from '@/services/api/letterDraftApiSlice';
import { useSnackbar } from 'notistack';
import { useStagePhotoMutation } from '@/services/api/letterApiSlice';
import { useStagedPhotoExpiry } from '@/app/hooks/useStagedPhotoExpiry';
import React, {
  ChangeEvent,
  FC,
  Fragment,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type Params = {
  id: string;
};

interface Props {
  isReply?: boolean;
}

export const CreateFormLetter: FC<CreateFormProps & Props> = (props: CreateFormProps & Props): ReactElement => {
  const { tittle, errorRedirectPath, nextFormPath, isMobile, mobileTittle, isReply } = props;
  let { previousFormPath, previousFormLabel } = props;

  const { id } = useParams<Params>();
  const dispatch: AppDispatch = useAppDispatch();
  const navigate: NavigateFunction = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);
  const department: DepartmentDTO | null = useAppSelector(getDepartment);
  const region: RegionDTO | null = useAppSelector(getRegion);
  const sender: SenderDTO | null = useAppSelector(getSender);
  const author: AuthorDTO | null = useAppSelector(getAuthor);
  const initialLetterText: string = useAppSelector(getLetterText) || '';

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [letterText, setLetterText] = useState<string>(initialLetterText);
  const [letterDraftId, setDraftId] = useState<number | undefined>(useAppSelector(getLetterDraftId));
  const [isEmoji, setEmoji] = useState<boolean>(false);
  const stagedPhotos: StagedPhotoDTO[] = useAppSelector(getStagedPhotos);
  useStagedPhotoExpiry();
  const [answerRequired, setAnswerRequired] = useState<boolean>(useAppSelector(getAnswerOrder));
  const [answersCount, setAnswersCount] = useState<number>(useAppSelector(getAnswersCount));

  const [sudokuAttached, setSudokuAttached] = useState<boolean>(useAppSelector(getSudokuAttached));
  const [sudokuCount, setSudokuCount] = useState<number>(useAppSelector(getSudokuCount));
  const [sudokuType, setSudokuType] = useState<SudokuType>(useAppSelector(getSudokuType));
  const [sudokuDifficulty, setSudokuDifficulty] = useState<SudokuDifficulty>(useAppSelector(getSudokuDifficulty));
  const [crosswordAttached, setCrosswordAttached] = useState<boolean>(useAppSelector(getCrosswordAttached));
  const [crosswordCount, setCrosswordCount] = useState<number>(useAppSelector(getCrosswordCount));
  const [newsAttached, setNewsAttached] = useState<boolean>(useAppSelector(getNewsAttached));
  const [sportsNewsAttached, setSportsNewsAttached] = useState<boolean>(useAppSelector(getSportsNewsAttached));

  const [letterTextError, setLetterTextError] = useState<boolean>(false);

  const refDraftButton = useRef<HTMLButtonElement>(null);

  const [saveLetterDraft, { isLoading: isLetterDraftSaving }] = useSaveLetterDraftMutation();
  const [stagePhoto, { isLoading: isPhotoStaging }] = useStagePhotoMutation();
  const { data: clientInfo } = useGetClientInfoQuery();

  const charactersPerPage: number = clientInfo?.charactersPerPage ? clientInfo.charactersPerPage : 2500;
  // The server rejects a letter above `photoMaxCount`, so the counter reads the same number.
  const maxPhotoCount: number = clientInfo?.photoMaxCount ?? DEFAULT_MAX_PHOTO_COUNT;
  const sudokuTypeOptions: SudokuType[] = useMemo(
    () => resolveSudokuTypeOptions(clientInfo?.sudokuAllowedTypes),
    [clientInfo?.sudokuAllowedTypes],
  );

  const photoCost: number = getPhotoCost(!!authUser, department);
  const sudokuCost: number = getSudokuCost(!!authUser, department);
  const crosswordCost: number = getCrosswordCost(!!authUser, department);
  const newsCost: number = getNewsCost(!!authUser, department);
  const letterCost: number = getLetterCost(!!authUser, department);
  const additionalCost: number = calculateAdditionalCost(
    answerRequired,
    answersCount,
    letterCost,
    stagedPhotos.length,
    crosswordAttached,
    crosswordCount,
    sudokuAttached,
    sudokuCount,
    newsAttached,
    sportsNewsAttached,
    photoCost,
    sudokuCost,
    crosswordCost,
    newsCost,
  );

  const submitDisabled: boolean =
    (isBlank(letterText) &&
      stagedPhotos.length === 0 &&
      !answerRequired &&
      !crosswordAttached &&
      !sudokuAttached &&
      !newsAttached &&
      !sportsNewsAttached) ||
    !department?.allowSending ||
    !answersCount ||
    answersCount < 1 ||
    answersCount > 10 ||
    (sudokuAttached && (!sudokuCount || sudokuCount < 1 || sudokuCount > 5)) ||
    (crosswordAttached && (!crosswordCount || crosswordCount < 1 || crosswordCount > 5)) ||
    isPhotoStaging;

  useEffect((): void => {
    if (!(department && region && sender && author)) {
      if (errorRedirectPath) {
        navigate(errorRedirectPath);
      }
    }
  }, [author, department, errorRedirectPath, navigate, region, sender]);

  if (isReply && id) {
    previousFormLabel = `Письмо № ${id}`;
    previousFormPath = getInboxLetterRoute(parseInt(id));
  }

  const handleValidEmoji = useDebounce<string>((letterText: string): void => {
    setEmoji(emojiValidation(letterText));
  }, 1000);

  const handleLetterText = (letterText: string): void => {
    if (letterTextError && letterText.length < MAX_LETTER_COUNT) {
      setLetterTextError(false);
    } else if (letterText.length >= MAX_LETTER_COUNT) {
      setLetterTextError(true);
      enqueueSnackbar(`Письмо не может содержать больше ${MAX_LETTER_COUNT} символов`, {
        variant: 'error',
        preventDuplicate: true,
      });
    }
    setLetterText(letterText);
    handleValidEmoji(letterText);
  };

  const handleAttachPhoto = async (photo: File): Promise<void> => {
    await stagePhoto({ image: photo, departmentId: department?.id })
      .unwrap()
      .then((staged: PhotoStageDTO): void => {
        dispatch(addStagedPhoto({ id: staged.uuid, thumbnail: staged.thumbnail, stagedAt: Date.now() }));
      })
      .catch((e: FetchBaseQueryError | SerializedError): void => {
        // RTK Query preserves the HTTP status in `originalStatus` when the proxy returns HTML.
        const status = 'status' in e ? (e.status === 'PARSING_ERROR' ? e.originalStatus : e.status) : undefined;
        const data = 'data' in e ? e.data : undefined;
        const message =
          data && typeof data === 'object' && 'message' in data && typeof data.message === 'string'
            ? data.message || MESSAGE_FETCH_ERROR
            : MESSAGE_FETCH_ERROR;
        enqueueSnackbar(status === REQUEST_ENTITY_TOO_LARGE ? MESSAGE_PHOTO_SIZE_EXCEEDED : message, {
          variant: 'error',
        });
      });
  };

  const handleRemovePhoto = (id: string): void => {
    // The store drops the photo from the server once the form lets go of it.
    dispatch(removeStagedPhoto(id));
  };

  const handleCheckAnswerRequired = (): void => {
    if (answerRequired) {
      setAnswersCount(1);
    }
    setAnswerRequired(!answerRequired);
  };

  const handleCrosswordAttached = (e: ChangeEvent<HTMLInputElement>): void => {
    const checked: boolean = e.target.checked;
    if (checked) {
      setCrosswordCount(1);
    }
    setCrosswordAttached(checked);
  };

  const handleSudokuAttached = (e: ChangeEvent<HTMLInputElement>): void => {
    const checked: boolean = e.target.checked;
    if (checked) {
      setSudokuCount(1);
    }
    setSudokuAttached(checked);
  };

  const handleNewsAttached = (e: ChangeEvent<HTMLInputElement>): void => {
    const checked: boolean = e.target.checked;
    setNewsAttached(checked);
  };

  const handleSportsNewsAttached = (e: ChangeEvent<HTMLInputElement>): void => {
    const checked: boolean = e.target.checked;
    setSportsNewsAttached(checked);
  };

  const handleSaveLetterDraft = async (): Promise<void> => {
    dispatch(setLetterTextForm(letterText));
    dispatch(setSudokuAttachedState(sudokuAttached));
    dispatch(setSudokuCountState(sudokuCount));
    dispatch(setSudokuTypeState(sudokuType));
    dispatch(setSudokuDifficultyState(sudokuDifficulty));
    dispatch(setCrosswordAttachedState(crosswordAttached));
    dispatch(setCrosswordCountState(crosswordCount));
    dispatch(setNewsAttachedState(newsAttached));
    dispatch(setSportsNewsAttachedState(sportsNewsAttached));
    dispatch(setAnswerOrderedForm(answerRequired));
    dispatch(setAnswersCountForm(answersCount));
    const dto: SaveLetterDraftDTO = {
      id: letterDraftId,
      department: department?.id,
      author: author,
      sender: sender,
      letter: letterText,
      isAnswerOrdered: answerRequired,
      answersCount: answersCount,
      sudokuAttached: sudokuAttached,
      sudokuCount: sudokuCount,
      sudokuType: sudokuType,
      sudokuDifficulty: sudokuDifficulty,
      crosswordAttached: crosswordAttached,
      crosswordCount: crosswordCount,
      newsAttached: newsAttached,
      sportsNewsAttached: sportsNewsAttached,
      photoIds: stagedPhotos.map((photo: StagedPhotoDTO): string => photo.id),
    };
    await saveLetterDraft({
      letterDraft: dto,
    })
      .unwrap()
      .then((letterDraftId: LetterDraftIdDTO): void => {
        console.log(letterDraftId);
        dispatch(setLetterDraftId(letterDraftId.id));
        setDraftId(letterDraftId.id);
      })
      .catch((e: { status: number; data: ErrorDTO }): void => {
        enqueueSnackbar(e.data.message ? e.data.message : MESSAGE_FETCH_ERROR, {
          variant: 'error',
        });
      });
  };

  const saveDraftTimer = useCallback((): void => {
    if (authUser && refDraftButton && refDraftButton.current) {
      refDraftButton.current.click();
    }
  }, [authUser]);

  const handleSubmit = (): void => {
    dispatch(setLetterTextForm(letterText));
    dispatch(setSudokuAttachedState(sudokuAttached));
    dispatch(setSudokuCountState(sudokuCount));
    dispatch(setSudokuTypeState(sudokuType));
    dispatch(setSudokuDifficultyState(sudokuDifficulty));
    dispatch(setCrosswordAttachedState(crosswordAttached));
    dispatch(setCrosswordCountState(crosswordCount));
    dispatch(setNewsAttachedState(newsAttached));
    dispatch(setSportsNewsAttachedState(sportsNewsAttached));
    dispatch(setAnswerOrderedForm(answerRequired));
    dispatch(setAnswersCountForm(answersCount));
    dispatch(clearBalanceByLetterPageState());
    if (nextFormPath) {
      navigate(nextFormPath);
    }
  };

  useEffect(() => {
    const timer: ReturnType<typeof setInterval> = setInterval(saveDraftTimer, 15000);
    return (): void => {
      clearInterval(timer);
    };
  }, [saveDraftTimer]);

  return (
    <Fragment>
      <Grid
        container={true}
        direction={'column'}
        sx={{
          paddingBottom: (theme: Theme): string => (isMobile ? theme.spacing(6) : theme.spacing(0)),
        }}>
        <Grid item={true} mb={isMobile ? 1 : 2}>
          <PageHeader
            tittle={tittle}
            previousFormLabel={previousFormLabel}
            previousFormPath={previousFormPath}
            isMobile={isMobile}
            mobileTittle={mobileTittle}
          />
        </Grid>
        {(!isMobile || isReply) && (
          <Grid item={true} mb={3}>
            <TittleInfo
              region={region?.name}
              department={department?.name}
              prisonerFirstName={sender?.firstName}
              prisonerMiddleName={sender?.middleName}
              prisonerLastName={sender?.lastName}
              isChanged={isReply}
              isMobile={isMobile}
            />
          </Grid>
        )}
        {isReply && department?.message && (
          <Grid item={true} mb={3}>
            <LetterAlert error={!department.allowSending}>{department.message}</LetterAlert>
          </Grid>
        )}
        <Grid item={true} marginBottom={isMobile ? '48px' : '13px'}>
          <Grid container={true} justifyContent={isMobile ? 'flex-start' : 'flex-end'}>
            <Grid
              item={true}
              sx={{
                cursor: 'pointer',
                color: (theme: Theme) => theme.colors.primary,
              }}
              onClick={() => setOpenDialog(true)}>
              <Grid container={true} columnSpacing={0.5} alignItems={'center'}>
                <Grid item={true}>
                  <InfoIcon sx={{ display: 'block', width: '16px', height: '16px' }} />
                </Grid>
                <Grid item={true}>
                  <Typography variant={'body2'}>{'Какие письма не пройдут цензуру?'}</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item={true}>
          <LetterTextField
            label={'Текст письма'}
            size={'medium'}
            name={'letterText'}
            value={letterText}
            error={letterTextError}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
              handleLetterText(event.target.value);
            }}
            countCharacters={letterText.length}
            costPage={letterCost}
            charactersPerPage={charactersPerPage}
            additionalCost={additionalCost}
            rows={isMobile ? 7 : 11}
            inputProps={{ maxLength: MAX_LETTER_COUNT }}
          />
        </Grid>
        {isEmoji && (
          <Grid item={true} mt={isMobile ? 3 : 2}>
            <LetterAlert isWarn={true}>
              <Typography variant={'body1'} component={'div'}>
                {'Латинские буквы, эмодзи и специальные символы могут быть расценены цензором как тайнопись. '}
                <TypographyLink onClick={() => setOpenDialog(true)}>{'Подробнее'}</TypographyLink>
              </Typography>
            </LetterAlert>
          </Grid>
        )}
        <Grid item={true} mt={isMobile ? 3 : 4}>
          <Grid container={true} justifyContent={'space-between'} columnSpacing={8} rowGap={isMobile ? 8 : 0}>
            <Grid item={true} sm={8}>
              <Grid container={true} direction={'column'} spacing={isMobile ? 1.5 : 2}>
                <Grid item={true}>
                  <CreateFormAttachments
                    photoCost={photoCost}
                    sudokuCost={sudokuCost}
                    crosswordCost={crosswordCost}
                    newsCost={newsCost}
                    allowAttachments={department?.allowPhoto}
                    allowSudoku={department?.allowSudoku}
                    allowCrossword={department?.allowCrossword}
                    allowNews={department?.allowNews}
                    photos={stagedPhotos}
                    maxPhotoCount={maxPhotoCount}
                    isPhotoStaging={isPhotoStaging}
                    onAttachPhoto={handleAttachPhoto}
                    onRemovePhoto={handleRemovePhoto}
                    sudokuAttached={sudokuAttached}
                    onSudokuAttached={handleSudokuAttached}
                    sudokuCount={sudokuCount}
                    onSudokuCountChange={setSudokuCount}
                    sudokuType={sudokuType}
                    sudokuTypeOptions={sudokuTypeOptions}
                    onSudokuTypeChange={setSudokuType}
                    sudokuDifficulty={sudokuDifficulty}
                    onSudokuDifficultyChange={setSudokuDifficulty}
                    crosswordAttached={crosswordAttached}
                    onCrosswordAttached={handleCrosswordAttached}
                    crosswordCount={crosswordCount}
                    onCrosswordCountChange={setCrosswordCount}
                    newsAttached={newsAttached}
                    onNewsAttached={handleNewsAttached}
                    sportsNewsAttached={sportsNewsAttached}
                    onSportsNewsAttached={handleSportsNewsAttached}
                    isMobile={isMobile}
                  />
                </Grid>
                <Grid item={true}>
                  <Grid container={true} direction={'column'} spacing={1}>
                    <Grid item={true}>
                      <LabeledCheckBox
                        label={'Получить ответ'}
                        fontWeight={500}
                        checked={answerRequired}
                        onClick={handleCheckAnswerRequired}
                      />
                    </Grid>
                    {answerRequired && (
                      <>
                        <Grid item={true}>
                          <Typography variant={'body2'} sx={{ color: (theme: Theme) => theme.colors.grayText }}>
                            {
                              'Выберите количество бланков, каждый из которых будет отправлен как отдельное письмо. Каждый ответ будет отображаться отдельно в личном кабинете.'
                            }
                          </Typography>
                        </Grid>
                        <Grid item={true} mt={1}>
                          <Grid container={true} direction={'row'} spacing={2} alignItems={'center'}>
                            <Grid item={true}>
                              <AnswerCountSelectorField
                                value={answersCount}
                                minValue={0}
                                maxValue={10}
                                step={1}
                                onValueChange={setAnswersCount}
                              />
                            </Grid>
                            <Grid item={true}>
                              <Typography variant={'h4'}>{`+ ${formatCurrency(
                                letterCost * answersCount,
                                0,
                              )}`}</Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {isMobile && (
              <MobileBar>
                <FixedButton color={'primary'} variant={'contained'} disabled={submitDisabled} onClick={handleSubmit}>
                  {'Продолжить'}
                </FixedButton>
              </MobileBar>
            )}
            <Grid item={true} sm={4}>
              <Grid container={true} direction={'column'} spacing={3}>
                {!isMobile && (
                  <Grid item={true}>
                    <Button
                      fullWidth={true}
                      color={'primary'}
                      variant={'contained'}
                      size={'medium'}
                      disabled={submitDisabled}
                      onClick={handleSubmit}>
                      {'Продолжить'}
                    </Button>
                  </Grid>
                )}
                <Grid item={true}>
                  <Grid container={true} direction={'column'} spacing={1}>
                    <Grid item={true}>
                      <Button
                        ref={refDraftButton}
                        fullWidth={true}
                        variant={'outlined'}
                        onClick={handleSaveLetterDraft}
                        disabled={isLetterDraftSaving || !authUser}>
                        {'Сохранить как черновик'}
                      </Button>
                    </Grid>
                    {!authUser && (
                      <Grid item={true}>
                        <Typography variant={'body2'} sx={{ color: (theme: Theme) => theme.colors.grayText }}>
                          {'Черновик доступен для авторизованных пользователей.'}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <LetterRulesDialog
        open={openDialog}
        isMobile={isMobile}
        handleCloseButton={() => setOpenDialog(false)}
        onClose={() => setOpenDialog(false)}
      />
    </Fragment>
  );
};
